import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import styles from './style.module.scss'; // Replace with actual path

// Dynamic imports

const CardDots = dynamic(
  () => import("@/Components/DataDisplayCompoants/CardDots"),
  { ssr: false },
);
import Section from "@/Components/LayoutComponents/Section";
import ButtonLink from "@/Components/ButtonLink";
import { toWebp } from "@/utils/common";

type OfferItem = {
  label: string;
  link: string;
  //   image: string;
};

type ReadMore = {
  label: string;
  href: string;
};

type ListSectionProps = {
  heading: string;
  sections: ListSectionBlock[];
};

type ListSectionBlock = {
  offerTitle?: string;
  offerItems: OfferItem[];
  WhatWeOffer: string;
  readMore?: ReadMore;
  caseStudyTitle?: string;
  caseStudyText: string;
  caseStudySubtitle: string;
  caseStudyImage: string;
  caseStudyButtonLink: string;
  caseStudyButtonLabel: string;
};

const ListSection: React.FC<ListSectionProps> = ({ heading, sections }) => {
  const slugify = (text: string) =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "") // remove special chars
      .replace(/\s+/g, "-"); // spaces → dash

  return (
    <Section>
      <div className="container">
        <div className="flex justify-between items-start lg:flex-row flex-col lg:gap-[30px]  gap-6">
          {/* Left Sticky Heading */}
          <div className="xl:w-[390px] lg:w-[266px] w-full flex-[0_0_auto] lg:sticky top-[230px]">
            <h2 className="text-heading-2 font-bold text-primary-text lg:text-left text-center">
              {heading}
            </h2>
          </div>

          {/* Right Content */}
          <div className="lg:w-[70%] w-full" id={slugify(heading)}>
            {sections.map((section, index) => (
              <div
                key={index}
                className={`${index > 0 ? "xl:mt-12 lg:mt-10 mt-[30px] border-t-2 border-border xl:pt-12 lg:pt-10 pt-[30px]" : "lg:border-0 border-t-2 border-border lg:pt-0 pt-[30px]"} `}
              >
                {/* Offer Section */}
                {section.offerTitle && (
                  <h3 className="text-heading-3 font-bold text-primary-text lg:mb-6 mb-[30px] lg:text-left text-center">
                    {section.offerTitle}
                  </h3>
                )}

                <div className="flex justify-start items-center gap-3.5 bg-blue-50 py-3 px-2">
                  <Image
                    src="/images/ServicesPage/heading-1.svg"
                    alt="offer icon"
                    width={32}
                    height={32}
                    className="w-8 h-8 flex-shrink-0"
                    loading="eager"
                  />
                  <h3 className="xl:text-heading-6 text-subtitle-1 font-bold text-blue-500">
                    {section.WhatWeOffer}
                  </h3>
                </div>

                <div className="grid sm:grid-cols-2 grid-cols-1 xl:gap-[23px_30px] lg:gap-[24px_30px] gap-6 xl:mt-5 lg:mt-8 mt-5">
                  {section?.offerItems?.map((item, itemIndex) => {
                    return item?.link && item?.label ? (
                      <React.Fragment key={itemIndex}>
                        <div className="group relative transition-all duration-300 bg-[linear-gradient(179.61deg,_#8ABC00_3.58%,_#0086E6_97.78%)] hover:rounded-bl-[10px] focus-within:rounded-bl-[10px] hover:ps-[3px] focus-within:ps-[3px] border-b-[1px] border-border hover:border-[#0086E6F9] focus-within:border-[#0086E6F9]">
                          <Link
                            href={item?.link}
                            className="bg-white h-full flex justify-between items-center transition-all duration-300 group-hover:rounded-bl-[10px] group-focus-within:rounded-bl-[10px] p-2 ps-2 pb-2 group-hover:ps-[5px] group-focus-within:ps-[5px] text-subtitle-2 font-medium text-secondary-text group-hover:text-primary-text group-focus-within:text-primary-text capitalize"
                          >
                            {item?.label}
                            <Image
                              src="/images/ServicesPage/right-arrow.svg"
                              alt="right arrow"
                              width={24}
                              height={24}
                              className="transition-all duration-300 scale-0 ease-in-out group-hover:scale-100 group-focus-within:scale-100 origin-right w-6 h-6 flex-shrink-0"
                              loading="eager"
                            />
                          </Link>
                        </div>


                      </React.Fragment>
                    ) : null;
                  })}
                </div>
                <div>
                  {
                    section?.readMore?.href && section?.readMore?.label && (
                      <Link
                        href={section?.readMore?.href}
                        className={`${styles?.hoverLink} inline-block mt-[24px] text-[#0086E6] text-[16px] leading-[22.2px] lg:text-[18px] lg:leading-[25.2px] font-semibold`}
                      >
                        {section?.readMore?.label}
                        <Image
                          src="/images/ServicesPage/arrow-blue-icon.svg"
                          alt="arrow left"
                          width={20}
                          height={20}
                          className="ml-[10px] inline w-[20px] h-[20px] flex-shrink-0"
                          loading="eager"
                        />
                      </Link>
                    )
                  }
                </div>


                {/* Case Study */}

                <div
                  className={`flex justify-start items-center gap-3.5 bg-green-50 py-3 px-2 ${index > 0 ? "xl:mt-16" : "xl:mt-12"
                    } lg:mt-8 mt-10`}
                >
                  <Image
                    src="/images/ServicesPage/heading-2.svg"
                    alt="case study icon"
                    width={32}
                    height={32}
                    className="w-8 h-8 flex-shrink-0"
                    loading="eager"
                  />
                  <h3 className="xl:text-heading-6 text-subtitle-1 font-bold text-green-900">
                    {section?.caseStudyText}
                  </h3>
                </div>

                <div className="mt-6 shadow-[0px_12.68px_57.07px_-7.93px_#001A2D3D] relative group overflow-hidden xl:max-h-[393px]">
                  <span className="bg-[linear-gradient(180deg,_rgba(0,_0,_0,_0.8)_39.98%,_rgba(0,_0,_0,_0.16)_100%)] absolute top-0 right-0 w-full h-full z-10"></span>
                  <CardDots />
                  <Image
                    src={toWebp(section?.caseStudyImage)}
                    alt="case study bg"
                    width={810}
                    height={393}
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 70vw, 810px"
                    className="w-full xl:h-[420px] h-[393px] max-h-full object-cover object-center transition-all duration-300 scale-100 ease-in-out group-hover:scale-[1.1] group-focus-within:scale-100"
                    style={{ aspectRatio: '810 / 393' }}
                    loading="lazy"
                    placeholder="blur"
                    blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODEwIiBoZWlnaHQ9IjM5MyIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iODEwIiBoZWlnaHQ9IjM5MyIgZmlsbD0iI2YzZjRmNiIvPjwvc3ZnPg=="
                  />
                  <div className="absolute top-0 left-0 py-[25px] max-w-[720px] px-5 z-20">
                    {/* <p className="text-subtitle-1 font-bold text-secondary-text-white">
                      {section.caseStudyText}
                    </p> */}
                    <p className="text-subtitle-1 font-bold text-secondary-text-white">
                      {section.caseStudySubtitle}
                    </p>
                    <div className="mt-3.5">
                      {section?.caseStudyButtonLabel && section?.caseStudyButtonLink && (
                        <ButtonLink
                          label={section?.caseStudyButtonLabel}
                          href={section?.caseStudyButtonLink}
                          className="text-subtitle-4"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
};
export default ListSection;